import { format, isToday, isYesterday, isSameDay } from 'date-fns'
import type { Activity, Message } from '../types/activity'

export interface ThreadMessage extends Message {
  activityId: string
}

export interface ThreadDay {
  key: string
  label: string
  messages: ThreadMessage[]
}

function dayLabel(iso: string): string {
  const date = new Date(iso)
  if (isToday(date)) return 'Today'
  if (isYesterday(date)) return 'Yesterday'
  return format(date, 'EEEE, MMM d')
}

/**
 * Flattens every logged text message into one continuous conversation,
 * oldest first, so the thread reads like a messaging app rather than a set
 * of disconnected timeline entries.
 */
export function buildSmsThread(activities: Activity[]): ThreadDay[] {
  const messages: ThreadMessage[] = activities
    .filter((activity) => activity.channel === 'sms')
    .flatMap((activity) =>
      (activity.messages ?? []).map((message) => ({
        ...message,
        activityId: activity.id,
      })),
    )
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())

  const days: ThreadDay[] = []

  messages.forEach((message) => {
    const last = days[days.length - 1]
    if (last && isSameDay(new Date(last.key), new Date(message.at))) {
      last.messages.push(message)
      return
    }
    days.push({
      key: message.at,
      label: dayLabel(message.at),
      messages: [message],
    })
  })

  return days
}

export function countThreadMessages(days: ThreadDay[]): number {
  return days.reduce((total, day) => total + day.messages.length, 0)
}
